// run-interrupt-signal-handler.ts
//
// Phase 05 (AC-02): SIGINT/SIGTERM handler for `avatar autopilot run`. First
// signal sets the interrupted flag, TERMs the active worker group, then
// KILLs it after a grace period (read via a getter AT SIGNAL TIME, never a
// hardcoded literal — the operator's config may set shutdown_grace_sec).
// Second signal force-exits immediately (130), skipping grace.
// CXR-01: recipes (verification/deploy) run detached in their own process
// group, so the terminal's Ctrl-C no longer reaches them — the same TERM/KILL
// is forwarded to every group in active-recipe-process-groups.ts.
// Code review P1 L2: detached recipes no longer get the terminal's SIGHUP
// either. On SIGHUP the run stops the worker + recipe groups, releases its
// locks, then re-raises SIGHUP with its own listener removed — avatar exits
// exactly as a hang-up always made it exit (no listener stays to keep it alive).
// Codex rereview CXR-R1 (2026-10-02): the groups are SNAPSHOTTED when the
// signal arrives and stopped TERM → grace → KILL → reaped on that snapshot.
// The registry is not re-read for them: a recipe whose leader died on TERM is
// unregistered by its runner while a same-group child that ignored TERM is
// still alive. `drain()` lets the CLI wait for that stop before it releases
// the lock or exits; `dispose()` never cancels it.
import { activeRecipeProcessGroups } from "./active-recipe-process-groups.js";
import { signalProcessGroup, terminateProcessGroup } from "./process-group-reaping.js";

export interface InstallRunInterruptHandlersInput {
  currentWorker: () => { pid: number; pgid: number } | undefined;
  currentGraceMs: () => number;
  forceCleanup: () => void;
  notify: (line: string) => void;
  /**
   * 06g R2c: awaited once the stopped groups are reaped (binding + active-worker
   * cleanup). Part of `stopping`, so `drain()` waits for it; on SIGHUP it runs
   * in a `.then` BEFORE the synchronous `.finally` that re-raises (debate B9).
   * A rejection is swallowed — cleanup must never keep a run alive.
   */
  afterWorkersStopped?: () => Promise<void>;
  /**
   * 06g R2c (SIGHUP): resolves when the run flow has settled (loop exited after
   * the attempt's archive + reset). The re-raise waits for it, capped by
   * RUN_SETTLE_WAIT_MAX_MS so a stuck run still exits as a hang-up.
   */
  waitForRunToSettle?: () => Promise<void>;
  /** 06k C5: con `run --detach` bỏ qua SIGHUP (đóng cửa sổ chat); SIGINT/SIGTERM giữ nguyên. */
  ignoreHangup?: boolean;
}

/** Upper bound on the SIGHUP wait for the run flow — same order as the git timeout. */
const RUN_SETTLE_WAIT_MAX_MS = 30_000;

export interface RunInterruptHandlers {
  isInterrupted: () => NodeJS.Signals | undefined;
  /**
   * Resolves once every group stopped for a signal (SIGHUP's included) is gone,
   * plus any recipe group still registered when it runs.
   */
  drain: () => Promise<void>;
  dispose: () => void;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// The signal the installed handler received, readable where the handler is not
// passed down (the release leg checks it right before deploy — CXR-R1 / Q1).
let receivedSignal: NodeJS.Signals | undefined;

/** The interrupt the running `run` received, if any; cleared when its handler is disposed. */
export function runInterruptReceived(): NodeJS.Signals | undefined {
  return receivedSignal;
}

export function installRunInterruptHandlers(
  input: InstallRunInterruptHandlersInput,
): RunInterruptHandlers {
  let signal: NodeJS.Signals | undefined;
  let hangingUp = false;
  receivedSignal = undefined;
  // `stopped` only dedupes; `inFlight` is what a second signal may still KILL —
  // a group already reaped is never signalled again (its pgid may be reused).
  const stopped = new Set<number>();
  const inFlight = new Set<number>();
  let stopping: Promise<void> = Promise.resolve();

  /** Worker group + every running recipe group, minus those already being stopped. */
  const snapshot = (): number[] => {
    const worker = input.currentWorker();
    const pgids = [...(worker ? [worker.pgid] : []), ...activeRecipeProcessGroups()];
    return [...new Set(pgids)].filter((pgid) => !stopped.has(pgid));
  };

  const afterStopped = (): Promise<void> =>
    Promise.resolve()
      .then(() => input.afterWorkersStopped?.())
      .catch(() => undefined);

  const stopGroups = (pgids: number[]): Promise<void> => {
    const graceMs = input.currentGraceMs();
    const stops = pgids.map((pgid) => {
      stopped.add(pgid);
      inFlight.add(pgid);
      return terminateProcessGroup(pgid, graceMs, sleep).finally(() => inFlight.delete(pgid));
    });
    return Promise.all(stops).then(() => undefined);
  };

  // Groups that appeared after the signal (a worker or recipe spawned before
  // the loop saw the flag). Returns whether anything was stopped.
  const stopLateGroups = async (): Promise<boolean> => {
    let stoppedAny = false;
    for (let late = snapshot(); late.length > 0; late = snapshot()) {
      await stopGroups(late);
      stoppedAny = true;
    }
    return stoppedAny;
  };

  const onSignal = (sig: NodeJS.Signals): void => {
    if (signal !== undefined) {
      // Second signal: force exit immediately, skip grace.
      for (const pgid of [...inFlight, ...snapshot()]) signalProcessGroup(pgid, "SIGKILL");
      try {
        input.forceCleanup();
      } catch {
        // never let cleanup failure block the forced exit
      }
      process.exit(130);
      return;
    }
    signal = sig;
    receivedSignal = sig;
    input.notify(`Nhận ${sig} — đang dừng worker và nhả lock (${sig} lần nữa để thoát ngay).`);
    stopping = stopGroups(snapshot()).then(afterStopped);
  };

  const onHangup = (): void => {
    if (hangingUp) return;
    hangingUp = true;
    signal ??= "SIGHUP";
    receivedSignal ??= "SIGHUP";
    stopping = Promise.all([stopping, stopGroups(snapshot())])
      .then(stopLateGroups)
      .then(afterStopped)
      .then(() =>
        Promise.race([input.waitForRunToSettle?.(), sleep(RUN_SETTLE_WAIT_MAX_MS)]).then(
          () => undefined,
        ),
      )
      .finally(() => {
        try {
          input.forceCleanup();
        } catch {
          // never let cleanup failure keep a hung-up run alive
        }
        process.removeListener("SIGHUP", onHangup);
        process.kill(process.pid, "SIGHUP");
      });
  };

  process.on("SIGINT", onSignal);
  process.on("SIGTERM", onSignal);
  const hangup = input.ignoreHangup === true ? () => {} : onHangup;
  process.on("SIGHUP", hangup);

  return {
    isInterrupted: () => signal,
    drain: async () => {
      if (signal === undefined) return;
      // A SIGHUP after SIGINT replaces `stopping` — wait for whichever is last.
      let awaited: Promise<void>;
      do {
        awaited = stopping;
        await awaited;
      } while (awaited !== stopping);
      // Review 06g M4: a worker spawned between the signal and this drain
      // wrote binding + active-worker.json AFTER the chained hook ran — run
      // the hook again so the next run never needs the reclaim path.
      if (await stopLateGroups()) await afterStopped();
    },
    dispose: () => {
      process.removeListener("SIGINT", onSignal);
      process.removeListener("SIGTERM", onSignal);
      process.removeListener("SIGHUP", hangup);
      receivedSignal = undefined;
    },
  };
}
